/**
 * Agents card for the platform-admin reseller-detail page. Renders the
 * commission roster for one reseller plus an inline form to add /
 * disable. Edit-in-place lives in a small modal because three fields
 * inline cluttered the row visually.
 *
 * Visibility: caller decides — this component assumes `isPlatformAdmin
 * && customer.type === 'reseller'` is already true.
 */

import { type FormEvent, type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Badge, Button, Card, Input, Skeleton } from '@/components/ui';
import {
  useAgents,
  useCreateAgent,
  useDisableAgent,
  useUpdateAgent,
  type AgentBasis,
  type AgentCommission,
} from '@/hooks/useAdminAgents';
import { ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

interface AgentsCardProps {
  resellerID: number;
  lang: string;
}

export function AgentsCard({ resellerID, lang }: AgentsCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const list = useAgents(resellerID);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AgentCommission | null>(null);
  const [error, setError] = useState<string | null>(null);

  const items = list.data?.items ?? [];
  const activeItems = items.filter((a) => a.is_active);
  const totalActivePct = activeItems.reduce((sum, a) => sum + a.percentage, 0);
  const remainingPct = Math.max(0, 100 - totalActivePct);

  return (
    <Card padding="md">
      <div className="flex items-start justify-between mb-3.5 gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-text">
            {t('aac_title')}
            <span className="num text-text-3 font-normal ml-1.5">{items.length}</span>
          </div>
          <p className="text-[11.5px] text-text-3 mt-0.5 leading-snug">
            {t('aac_help', {
              total: totalActivePct.toFixed(1),
              remaining: remainingPct.toFixed(1),
            })}
          </p>
        </div>
        {!adding && remainingPct > 0 && (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Icon.plus width="12" height="12" />
            {t('aac_add')}
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-3 px-2 py-1.5 bg-danger-bg border border-danger-bd rounded text-[12px] text-danger">
          {error}
        </div>
      )}

      {list.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : items.length === 0 && !adding ? (
        <p className="text-[12px] text-text-4 italic">{t('aac_empty')}</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((a) => (
            <AgentRow
              key={a.id}
              agent={a}
              lang={lang}
              onEdit={() => {
                setEditing(a);
                setError(null);
              }}
              onError={setError}
            />
          ))}
        </div>
      )}

      {adding && (
        <AddAgentForm
          resellerID={resellerID}
          remainingPct={remainingPct}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
          onCreated={() => {
            setAdding(false);
            setError(null);
          }}
          onError={setError}
        />
      )}

      {editing && (
        <EditAgentModal
          agent={editing}
          remainingPctIfActive={Math.max(0, 100 - (totalActivePct - editing.percentage))}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Row — one agent line in the roster
// ---------------------------------------------------------------------------

interface AgentRowProps {
  agent: AgentCommission;
  lang: string;
  onEdit: () => void;
  onError: (msg: string) => void;
}

function AgentRow({ agent, lang, onEdit, onError }: AgentRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const disable = useDisableAgent();

  const onDisable = async (): Promise<void> => {
    if (!window.confirm(t('aac_disable_confirm', { email: agent.user_email }))) return;
    try {
      await disable.mutateAsync({ id: agent.id, resellerID: agent.reseller_id });
    } catch (err) {
      onError(err instanceof ApiError ? err.message : t('aac_err_generic'));
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-line-soft text-[12.5px]',
        agent.is_active ? 'bg-surface-2' : 'bg-surface-3 opacity-70',
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-text font-medium truncate">{agent.user_email}</span>
          {!agent.is_active && <Badge variant="neutral">{t('aac_disabled')}</Badge>}
        </div>
        <div className="text-[11px] text-text-3 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="num font-semibold text-text-2">
            {agent.percentage.toFixed(1)}%
          </span>
          <span>·</span>
          <span>
            {agent.basis === 'gross_revenue'
              ? t('aac_basis_gross')
              : t('aac_basis_net')}
          </span>
          <span>·</span>
          <span>
            {t('aac_since', { when: formatRelativeTime(agent.effective_from, lang) })}
          </span>
          {agent.notes && (
            <>
              <span>·</span>
              <span className="italic truncate max-w-[180px]" title={agent.notes}>
                {agent.notes}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="text-[11px] text-volt-500 hover:underline"
        >
          {t('aac_edit')}
        </button>
        {agent.is_active && (
          <button
            type="button"
            onClick={() => void onDisable()}
            disabled={disable.isPending}
            className="text-[11px] text-danger hover:underline disabled:opacity-50 ml-2"
          >
            {t('aac_disable')}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline create form
// ---------------------------------------------------------------------------

interface AddAgentFormProps {
  resellerID: number;
  remainingPct: number;
  onCancel: () => void;
  onCreated: () => void;
  onError: (msg: string) => void;
}

function AddAgentForm({
  resellerID,
  remainingPct,
  onCancel,
  onCreated,
  onError,
}: AddAgentFormProps): JSX.Element {
  const { t } = useTranslation('default');
  const create = useCreateAgent();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [percentage, setPercentage] = useState<string>('10');
  const [basis, setBasis] = useState<AgentBasis>('net_margin');
  const [notes, setNotes] = useState('');

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    onError('');
    const pct = Number(percentage);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      onError(t('aac_err_pct_range'));
      return;
    }
    if (pct > remainingPct + 0.001) {
      onError(t('aac_err_cap', { remaining: remainingPct.toFixed(1) }));
      return;
    }
    try {
      await create.mutateAsync({
        resellerID,
        email: email.trim(),
        full_name: fullName.trim(),
        percentage: pct,
        basis,
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : t('aac_err_generic'));
    }
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="mt-3 pt-3 border-t border-line-soft space-y-2"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input
          type="email"
          required
          placeholder={t('aac_field_email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={create.isPending}
        />
        <Input
          type="text"
          placeholder={t('aac_field_full_name')}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          disabled={create.isPending}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            inputMode="decimal"
            step="0.1"
            min="0.1"
            max={remainingPct.toString()}
            placeholder={t('aac_field_pct')}
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            disabled={create.isPending}
            className="num"
          />
          <span className="text-text-3 text-[12px]">%</span>
        </div>
        <select
          value={basis}
          onChange={(e) => setBasis(e.target.value as AgentBasis)}
          disabled={create.isPending}
          className="h-9 px-2 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500"
        >
          <option value="net_margin">{t('aac_basis_net')}</option>
          <option value="gross_revenue">{t('aac_basis_gross')}</option>
        </select>
        <Input
          type="text"
          placeholder={t('aac_field_notes_opt')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={create.isPending}
        />
      </div>
      <p className="text-[11px] text-text-4">
        {t('aac_hint_invite')}
      </p>
      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={onCancel}
          disabled={create.isPending}
        >
          {t('aac_cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? t('aac_creating') : t('aac_create')}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Edit modal — keeps the row stable + lets the operator change all
// fields at once. Cap is re-checked client-side using
// remainingPctIfActive (which already excludes the agent's own
// current percentage); the server re-checks atomically.
// ---------------------------------------------------------------------------

interface EditAgentModalProps {
  agent: AgentCommission;
  remainingPctIfActive: number;
  onClose: () => void;
  onSaved: () => void;
}

function EditAgentModal({
  agent,
  remainingPctIfActive,
  onClose,
  onSaved,
}: EditAgentModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const update = useUpdateAgent();

  const [percentage, setPercentage] = useState<string>(agent.percentage.toString());
  const [basis, setBasis] = useState<AgentBasis>(agent.basis);
  const [isActive, setIsActive] = useState(agent.is_active);
  const [notes, setNotes] = useState(agent.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSave = async (): Promise<void> => {
    setError(null);
    const pct = Number(percentage);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      setError(t('aac_err_pct_range'));
      return;
    }
    if (isActive && pct > remainingPctIfActive + 0.001) {
      setError(t('aac_err_cap', { remaining: remainingPctIfActive.toFixed(1) }));
      return;
    }
    try {
      await update.mutateAsync({
        id: agent.id,
        percentage: pct,
        basis,
        is_active: isActive,
        notes: notes.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('aac_err_generic'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!update.isPending) onClose();
      }}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[480px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {t('aac_edit_title')}
          </h2>
          <p className="mono text-[11px] text-text-3 mt-0.5">{agent.user_email}</p>
        </div>
        <div className="px-5 py-4 space-y-3 text-[13px]">
          {error && (
            <div className="px-2 py-1.5 bg-danger-bg border border-danger-bd rounded text-[12px] text-danger">
              {error}
            </div>
          )}
          <label className="block">
            <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
              {t('aac_field_pct')} (%)
            </span>
            <Input
              type="number"
              step="0.1"
              min="0.1"
              max="100"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              disabled={update.isPending}
              className="num"
            />
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
              {t('aac_field_basis')}
            </span>
            <select
              value={basis}
              onChange={(e) => setBasis(e.target.value as AgentBasis)}
              disabled={update.isPending}
              className="h-10 w-full px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500"
            >
              <option value="net_margin">{t('aac_basis_net')}</option>
              <option value="gross_revenue">{t('aac_basis_gross')}</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-text-2 mb-1.5 block">
              {t('aac_field_notes')}
            </span>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={update.isPending}
            />
          </label>
          <label className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={update.isPending}
            />
            <span className="text-[13px] text-text">{t('aac_field_active')}</span>
          </label>
        </div>
        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={update.isPending}
          >
            {t('aac_cancel')}
          </Button>
          <Button onClick={() => void onSave()} disabled={update.isPending}>
            {update.isPending ? t('aac_saving') : t('aac_save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
